import React, {useEffect, useState} from "react";
import {CalendarDays, FileText} from "lucide-react";
import {api, apiError} from "../lib/api";
import {can} from "../lib/permissions";
import {fmtDateTime} from "../lib/format";
import {Card} from "./ui/card";
import {Button} from "./ui/button";

// The server authorizes each setting by permission only, never by role.
const canEditSiteSetting = (user, config) => config.permissions.every(permission => can(user, permission));

// One site setting, saved per site with its revision and history.
const MONTH = {path: "site-pricing", field: "month_days", permissions: ["tariffs.read", "tariffs.manage"], idPrefix: "month-days", Icon: CalendarDays,
  title: "Durata del mese per la tariffa mensile",
  intro: "Vale per i nuovi preventivi e noleggi di tutta la sede. I giorni oltre i mesi interi si pagano a tariffa giornaliera. Preventivi e contratti già salvati non cambiano.",
  options: [[30, "Mese di 30 giorni"], [28, "Mese di 28 giorni"]], parse: Number,
  defaultText: "Valore predefinito: 30 giorni, mai modificato.",
  saved: data => `Durata del mese di ${data.site_name || "sede"}: ${data.month_days} giorni.`,
  change: item => `da ${item.from_days} a ${item.to_days} giorni`};
const HANDOVER_DOCUMENT = {path: "site-documents", field: "show_handover_actor", permissions: ["rentals.read", "rentals.manage"], idPrefix: "handover-actor", Icon: FileText,
  title: "Chi consegna e chi riceve nel verbale PDF",
  intro: "Se attivo, il verbale scrive chi ha confermato consegna e riconsegna, ad esempio “Consegnata in sede da Mario Rossi (Operatore)” o “Ricevuta da Luca Bianchi (Autista)”, con data e ora. Vale per tutta la sede e per ogni PDF scaricato da ora, anche di consegne già registrate.",
  options: [["true", "Mostra chi ha consegnato e ricevuto"], ["false", "Non mostrarlo"]], parse: value => value === true || value === "true",
  defaultText: "Valore predefinito: attivo, mai modificato.",
  saved: data => `Verbale di ${data.site_name || "sede"}: ${data.show_handover_actor ? "mostra" : "non mostra"} chi ha consegnato e ricevuto.`,
  change: item => `da ${item.from_actor ? "attivo" : "disattivo"} a ${item.to_actor ? "attivo" : "disattivo"}`};

function SiteRow({config, site, editable, onSaved}) {
  const [value, setValue] = useState(site[config.field]), [busy, setBusy] = useState(false), [error, setError] = useState("");
  useEffect(() => { setValue(site[config.field]); }, [site, config.field]);
  const id = `${config.idPrefix}-${site.site_id}`;
  async function save() {
    setBusy(true); setError("");
    try {
      const {data} = await api.put(`/${config.path}/${encodeURIComponent(site.site_id)}`, {[config.field]: config.parse(value), expected_revision: site.revision});
      onSaved(data, config.saved(data));
    } catch (failure) {
      setError(apiError(failure));
      if (failure.response?.status === 409) onSaved(null);
    } finally { setBusy(false); }
  }
  return <li className="space-y-2 rounded-lg border border-border p-3">
    <div className="flex flex-wrap items-end gap-3">
      <label className="block min-w-40 flex-1 space-y-1 text-sm" htmlFor={id}><span className="font-medium">{site.site_name || "Sede"}</span>
        <select id={id} className="min-h-11 w-full rounded-md border border-border bg-background p-2 text-base" value={String(value)} disabled={!editable || busy} onChange={event => setValue(config.parse(event.target.value))}>
          {config.options.map(([option, label]) => <option key={option} value={String(option)}>{label}</option>)}
        </select>
      </label>
      {editable && <Button type="button" className="min-h-11" disabled={busy || value === site[config.field]} onClick={save}>{busy ? "Salvataggio…" : "Salva"}</Button>}
    </div>
    <p className="text-sm text-muted-foreground">{site.is_default ? config.defaultText : `Ultima modifica: ${fmtDateTime(site.updated_at)}${site.updated_by?.name ? ` · ${site.updated_by.name}` : ""}`}</p>
    {error && <p role="alert" className="text-sm text-red-300">{error}</p>}
    {site.history?.length > 0 && <details className="text-sm"><summary className="min-h-11 cursor-pointer py-3">Storico modifiche ({site.history.length})</summary>
      <ul className="space-y-1 text-muted-foreground">{site.history.map(item => <li key={item.revision}>{fmtDateTime(item.at)} · {item.author?.name || "Utente"}: {config.change(item)}{item.from_default ? " (predefinito)" : ""}</li>)}</ul>
    </details>}
  </li>;
}

export default function SiteMonthSettings({user}) { return <SiteSettingPanel config={MONTH} user={user}/>; }
export function SiteHandoverDocumentSettings({user}) { return <SiteSettingPanel config={HANDOVER_DOCUMENT} user={user}/>; }

function SiteSettingPanel({config, user}) {
  const [sites, setSites] = useState(null), [error, setError] = useState(""), [notice, setNotice] = useState(""), [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const controller = new AbortController(); setError("");
    api.get(`/${config.path}`, {signal: controller.signal}).then(({data}) => { if (!controller.signal.aborted) setSites(data); })
      .catch(failure => { if (!controller.signal.aborted) setError(apiError(failure)); });
    return () => controller.abort();
  }, [attempt, config.path]);
  const editable = canEditSiteSetting(user, config);
  function saved(site, message) {
    if (!site) { setNotice(""); setAttempt(value => value + 1); return; }
    setSites(rows => rows.map(row => row.site_id === site.site_id ? site : row)); setNotice(message);
  }
  return <Card className="border-border bg-card p-6">
    <h3 className="mb-1 flex items-center gap-2 font-heading text-lg font-semibold"><config.Icon className="h-5 w-5 text-primary" aria-hidden="true"/> {config.title}</h3>
    <p className="mb-4 text-sm text-muted-foreground">{config.intro}</p>
    {error ? <div role="alert" className="space-y-3"><p>{error}</p><Button variant="outline" onClick={() => setAttempt(value => value + 1)}>Riprova</Button></div>
      : !sites ? <p role="status">Caricamento sedi…</p>
        : !sites.length ? <p role="status">Nessuna sede disponibile.</p>
          : <ul className="space-y-3">{sites.map(site => <SiteRow key={site.site_id} config={config} site={site} editable={editable} onSaved={saved}/>)}</ul>}
    {notice && <p role="status" className="mt-3 text-sm">{notice}</p>}
    {sites && !editable && <p className="mt-3 text-sm text-muted-foreground">Il tuo accesso non ha il permesso per cambiare questa impostazione.</p>}
  </Card>;
}
